/**
 * CameraCapture — live browser-camera capture for a single survey photo
 * question (web only). Opens the device camera via getUserMedia, captures a
 * still frame, and saves it bound to the question (``tag = questionCode``)
 * through the existing ``POST /photos`` pipeline (→ OneDrive when Graph is
 * configured). NO file uploads are offered — this is capture-only.
 *
 * Used by the 13 "Desktop Survey" templates (photo_ref questions flagged
 * ``config.camera_only``). The stored answer is the photo id.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, RefreshCw, Check, X, AlertTriangle, SwitchCamera } from 'lucide-react';
import { api, API_BASE } from '@/lib/api';
import axios from 'axios';
import { toast } from 'sonner';

interface Props {
  instructionId: string;
  token?: string;
  questionCode: string;
  caption?: string;
  value: string;                 // stored photo id (answer)
  onChange: (photoId: string) => void;
  testId: string;
  maxMb?: number;
  facing?: 'any' | 'front' | 'back';
  allowSwitch?: boolean;
}

export const CameraCapture: React.FC<Props> = ({ instructionId, token, questionCode, caption, value, onChange, testId, maxMb, facing = 'any', allowSwitch = true }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [live, setLive] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which camera is active. Seeded from the per-question default.
  const [curFacing, setCurFacing] = useState<'user' | 'environment'>(facing === 'front' ? 'user' : 'environment');

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const startCamera = useCallback(async (which?: 'user' | 'environment') => {
    setError(null);
    const want = which ?? curFacing;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This device/browser does not support camera capture.');
      return;
    }
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: want } }, audio: false,
      });
      streamRef.current = stream;
      setCurFacing(want);
      setLive(true);
      // attach after render
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); } }, 50);
    } catch (e: unknown) {
      const name = (e as { name?: string })?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setError('Camera permission denied. Enable camera access in your browser settings to take the photo.');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setError('No camera found on this device.');
      } else {
        setError('Could not start the camera. Please try again.');
      }
    }
  }, [curFacing]);

  const switchCamera = useCallback(() => {
    void startCamera(curFacing === 'user' ? 'environment' : 'user');
  }, [curFacing, startCamera]);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setBusy(true);
    try {
      // Downscale to a max edge of 1920px, then step JPEG quality down until
      // the encoded image fits under the configured size limit (default 2 MB).
      const maxEdge = 1920;
      const scale = Math.min(1, maxEdge / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const targetBytes = Math.max(0.3, maxMb || 2) * 1024 * 1024;
      let quality = 0.85;
      let dataUrl = canvas.toDataURL('image/jpeg', quality);
      while (dataUrl.length * 0.75 > targetBytes && quality > 0.4) {
        quality -= 0.1;
        dataUrl = canvas.toDataURL('image/jpeg', quality);
      }
      stopStream();
      const client = token ? axios.create({ baseURL: API_BASE }) : api;
      const r = await client.post('/photos', {
        instruction_id: instructionId, caption: caption || '', tag: questionCode, image_data_url: dataUrl,
      });
      setPreview(dataUrl);
      onChange(r.data.id);
      toast.success('Photo captured');
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Could not save the photo. Please retake.');
      setError('Saving failed — please retake.');
    } finally { setBusy(false); }
  }, [caption, instructionId, onChange, questionCode, stopStream, token, maxMb]);

  const retake = useCallback(() => {
    setPreview(null);
    onChange('');
    startCamera();
  }, [onChange, startCamera]);

  // Captured state (answer present)
  if (value && !live) {
    return (
      <div className="space-y-2" data-testid={testId}>
        <div className="flex items-center gap-2 text-sm text-green-700 font-body">
          <Check size={16} /> Photo captured
        </div>
        {preview && <img src={preview} alt="captured" className="max-h-40 rounded-md border border-border/60" />}
        <button type="button" onClick={retake} data-testid={`${testId}-retake`}
          className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-raptor-blue/40 text-raptor-blue text-sm">
          <RefreshCw size={14} /> Retake
        </button>
      </div>
    );
  }

  // Live camera
  if (live) {
    return (
      <div className="space-y-2" data-testid={testId}>
        <video ref={videoRef} playsInline muted className="w-full max-w-sm rounded-md border border-border/60 bg-black" />
        <div className="flex items-center gap-2">
          <button type="button" onClick={capture} disabled={busy} data-testid={`${testId}-capture`}
            className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-raptor-gradient text-white text-sm font-medium disabled:opacity-60">
            <Camera size={15} /> {busy ? 'Saving…' : 'Capture'}
          </button>
          <button type="button" onClick={stopStream} data-testid={`${testId}-cancel`}
            className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-border/60 text-raptor-navy/70 text-sm">
            <X size={15} /> Cancel
          </button>
          {allowSwitch && facing === 'any' && (
            <button type="button" onClick={switchCamera} disabled={busy} data-testid={`${testId}-switch`}
              className="inline-flex items-center gap-1 px-3 h-9 rounded-md border border-border/60 text-raptor-navy/70 text-sm ml-auto">
              <SwitchCamera size={15} /> {curFacing === 'user' ? 'Rear' : 'Front'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Idle
  return (
    <div className="space-y-2" data-testid={testId}>
      <button type="button" onClick={() => void startCamera()} data-testid={`${testId}-open`}
        className="inline-flex items-center gap-1 px-3 h-9 rounded-md bg-raptor-gradient text-white text-sm font-medium">
        <Camera size={15} /> Take photo
      </button>
      {error && (
        <div className="flex items-start gap-1 text-xs text-red-600 font-body">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}
    </div>
  );
};

export default CameraCapture;
